import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix default marker issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Recenter helper
const RecenterMap = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords) map.panTo(coords);
  }, [coords]);
  return null;
};

// Custom truck icon
const createTruckIcon = () =>
  new L.DivIcon({
    html: `
      <div style="
        background: rgba(255,255,255,0.9);
        border-radius: 50%;
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 6px rgba(0,0,0,0.3);
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7A3AED" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10 17h4V5H2v12h2"/>
          <path d="M18 17h2a2 2 0 0 0 2-2v-3h-4z"/>
          <path d="M18 5v4h4"/>
          <circle cx="6.5" cy="17.5" r="2.5"/>
          <circle cx="17.5" cy="17.5" r="2.5"/>
        </svg>
      </div>
    `,
    className: '',
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });

const ShipmentCard = ({ drivers2, fetchDrivers }) => {
  const [fullMapDriver, setFullMapDriver] = useState(null);
  const [driverPositions, setDriverPositions] = useState({});
  const animationRefs = useRef({});

  const t = (key) => {
    const translations = {
      'dashboard.shipmentId': `Driver:`,
      'orders.viewInFull': 'View Full Map',
    };
    return translations[key] || key;
  };

  // Smooth animate position from start to end over duration
  const animateMovement = (id, start, end, duration = 2000) => {
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const lat = start[0] + (end[0] - start[0]) * t;
      const lng = start[1] + (end[1] - start[1]) * t;

      setDriverPositions((prev) => ({
        ...prev,
        [id]: [lat, lng],
      }));

      if (t < 1) {
        animationRefs.current[id] = requestAnimationFrame(animate);
      }
    };

    if (animationRefs.current[id]) {
      cancelAnimationFrame(animationRefs.current[id]);
    }
    animationRefs.current[id] = requestAnimationFrame(animate);
  };

  // Update positions smoothly when drivers change
  useEffect(() => {
    if (!drivers2) return;

    drivers2.forEach((driver) => {
      const hasLocation =
        driver.location?.coordinates &&
        driver.location.coordinates.length === 2;

      const newCoords = hasLocation
        ? [driver.location.coordinates[1], driver.location.coordinates[0]]
        : [20.5937, 78.9629];

      const prevCoords = driverPositions[driver._id];
      if (!prevCoords) {
        // Initialize
        setDriverPositions((prev) => ({
          ...prev,
          [driver._id]: newCoords,
        }));
      } else if (
        prevCoords[0] !== newCoords[0] ||
        prevCoords[1] !== newCoords[1]
      ) {
        // Animate movement
        animateMovement(driver._id, prevCoords, newCoords);
      }
    });
  }, [drivers2]);

  // Fetch drivers every 10s
  useEffect(() => {
    fetchDrivers();
    const intervalId = setInterval(fetchDrivers, 10000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="flex flex-wrap gap-4">
      {drivers2?.map((driver) => {
        const coords = driverPositions[driver._id] || [20.5937, 78.9629];

        return (
          <div
            key={driver._id}
            className="bg-white text-black rounded-xl w-full sm:w-80 m-4"
          >
            <div className="flex justify-between items-center bg-[#1E1B2E] text-white text-sm px-4 py-2 rounded-t-lg">
              <span>
                {t('dashboard.shipmentId')} {driver.fullName}
              </span>
            </div>

            <div className="p-4 relative">
              <h3 className="font-bold">{driver.mobileNumber}</h3>

              <MapContainer
                center={coords}
                zoom={5}
                scrollWheelZoom={false}
                className="h-48 md:h-64 w-full rounded-md mb-3"
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="© OpenStreetMap contributors"
                />
                <Marker position={coords} icon={createTruckIcon()} />
                <RecenterMap coords={coords} />
              </MapContainer>

              <div className="flex justify-center">
                <button
                  onClick={() => setFullMapDriver(driver)}
                  className="text-[#7A3AED] border border-[#7A3AED] text-sm px-4 py-1 rounded-full"
                >
                  {t('orders.viewInFull')}
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Fullscreen truck map */}
      {fullMapDriver && (
        <div className="fixed inset-0 z-[1000] bg-black bg-opacity-80 flex justify-center items-center">
          <div className="relative w-full h-full md:w-3/4 md:h-3/4 bg-white rounded-lg">
            <button
              className="absolute top-2 right-2 z-[1000] bg-red-500 text-white px-4 py-1 rounded"
              onClick={() => setFullMapDriver(null)}
            >
              Close
            </button>

            <MapContainer
              center={[
                fullMapDriver.location.coordinates[1],
                fullMapDriver.location.coordinates[0],
              ]}
              zoom={10}
              scrollWheelZoom={true}
              className="w-full h-full rounded-lg"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="© OpenStreetMap contributors"
              />
              <Marker
                position={[
                  fullMapDriver.location.coordinates[1],
                  fullMapDriver.location.coordinates[0],
                ]}
                icon={createTruckIcon()}
              />
              <RecenterMap
                coords={[
                  fullMapDriver.location.coordinates[1],
                  fullMapDriver.location.coordinates[0],
                ]}
              />
            </MapContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentCard;
